import React, { useState, useEffect, useRef } from 'react';
import { FaGraduationCap, FaUser, FaSchool, FaCity, FaLock, FaEye, FaEyeSlash, FaUserPlus, FaCheck, FaArrowRight, FaArrowLeft, FaSpinner, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullname: '',
    username: '',
    grade: '',
    school: '',
    city: '',
    password: '',
    confirmPassword: '',
    terms: false
  });

  const [passwordMatch, setPasswordMatch] = useState(true);
  const [usernameValid, setUsernameValid] = useState(true);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  
  const usernameTimeoutRef = useRef(null);
  const API_BASE_URL = 'https://question-manager-backend.onrender.com/api';

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

   const validateUsername = (username) => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,30}$/;
    return usernameRegex.test(username);
  };

   const checkUsernameAvailability = async (username) => {
    if (!username || !validateUsername(username)) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/check-username/${username}`);
      const data = await response.json();
      
      setUsernameAvailable(data.available);
    } catch (error) {
      console.error('Error checking username:', error);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

   useEffect(() => {
    if (usernameTimeoutRef.current) {
      clearTimeout(usernameTimeoutRef.current);
    }

    if (formData.username && validateUsername(formData.username)) {
      usernameTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(formData.username);
      }, 500);  
    } else {
      setUsernameAvailable(null);
    }

    return () => {
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
    };
  }, [formData.username]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));

     if (name === 'password' || name === 'confirmPassword') {
      const password = name === 'password' ? value : formData.password;
      const confirmPassword = name === 'confirmPassword' ? value : formData.confirmPassword;
      setPasswordMatch(password === confirmPassword);
    }

     if (name === 'username') {
      const isValid = validateUsername(value);
      setUsernameValid(isValid);
      
       if (!isValid || value !== formData.username) {
        setUsernameAvailable(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateUsername(formData.username)) {
      alert('اسم المستخدم يجب أن يحتوي على 3-30 حرفاً (أحرف إنجليزية، أرقام و _ فقط)');
      return;
    }

    if (usernameAvailable === false) {
      alert('اسم المستخدم موجود مسبقاً، يرجى اختيار اسم آخر');
      return;
    }

    if (usernameAvailable === null && formData.username) {
      alert('يرجى الانتظار حتى يتم التحقق من اسم المستخدم');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert('كلمتا السر غير متطابقتين!');
      return;
    }

    if (formData.password.length < 8) {
      alert('يجب أن تكون كلمة السر 8 أحرف على الأقل!');
      return;
    }

    if (!formData.terms) {
      alert('يجب الموافقة على الشروط والأحكام!');
      return;
    }

    setLoading(true);

    const result = await register(formData);
    
    setLoading(false);

    if (result.success) {
      alert('تم إنشاء الحساب بنجاح!');
      window.location.href = '/';
    } else {
      alert(`فشل إنشاء الحساب: ${result.error}`);
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.fullname || !formData.username || !formData.grade || !formData.school || !formData.city) {
        alert('يرجى ملء جميع الحقول المطلوبة');
        return;
      }
      
      if (!validateUsername(formData.username)) {
        alert('اسم المستخدم يجب أن يحتوي على 3-30 حرفاً (أحرف إنجليزية، أرقام و _ فقط)');
        return;
      }

      if (usernameAvailable === false) {
        alert('اسم المستخدم موجود مسبقاً، يرجى اختيار اسم آخر');
        return;
      }

      if (usernameAvailable === null && formData.username) {
        alert('يرجى الانتظار حتى يتم التحقق من اسم المستخدم');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const steps = [
    { 
      icon: <FaUser className="text-sm" />, 
      label: 'المعلومات الأساسية', 
      active: currentStep >= 1,
      completed: currentStep > 1
    },
    { 
      icon: <FaCheck className="text-sm" />, 
      label: 'إنشاء الحساب', 
      active: currentStep >= 2,
      completed: currentStep > 2
    }
  ];

  const grades = [
    'اختر الصف',
    'الصف السادس',
    'الصف أول متوسط',
    'الصف ثاني متوسط',
    'الصف ثالث متوسط',
    
  ];

  const renderUsernameStatus = () => {
    if (!formData.username) return null;

    if (!usernameValid) {
      return (
        <div className="flex items-center gap-1 mt-1">
          <FaTimes className="text-red-500 text-xs" />
          <p className="text-xs text-red-500">
            اسم المستخدم يجب أن يحتوي على 3-30 حرفاً (أحرف إنجليزية، أرقام و _ فقط)
          </p>
        </div>
      );
    }

    if (checkingUsername) {
      return (
        <div className="flex items-center gap-1 mt-1">
          <FaSpinner className="text-blue-500 text-xs animate-spin" />
          <p className="text-xs text-blue-500">جاري التحقق من اسم المستخدم...</p>
        </div>
      );
    }

    if (usernameAvailable === true) {
      return (
        <div className="flex items-center gap-1 mt-1">
          <FaCheckCircle className="text-green-500 text-xs" />
          <p className="text-xs text-green-500">✓ اسم المستخدم متاح</p>
        </div>
      );
    }

    if (usernameAvailable === false) {
      return (
        <div className="flex items-center gap-1 mt-1">
          <FaTimes className="text-red-500 text-xs" />
          <p className="text-xs text-red-500">اسم المستخدم موجود مسبقاً</p>
        </div>
      );
    }

    return null;
  };

  const renderStep1 = () => (
    <div className="space-y-6 flex-1 flex flex-col justify-between">
      <div className="space-y-6">
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
           <div>
            <label htmlFor="fullname" className="block text-sm font-semibold text-gray-700 mb-2">
              <FaUser className="ml-1 text-[#320A6B] inline" />
              الاسم الكامل
            </label>
            <input
              type="text"
              id="fullname"
              name="fullname"
              required
              value={formData.fullname}
              onChange={handleInputChange}
              className="input-subtle w-full px-4 py-3.5 rounded-xl text-base"
              placeholder="أدخل اسمك الكامل"
            />
          </div>

           <div>
            <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
              <FaUser className="ml-1 text-[#320A6B] inline" />
              اسم المستخدم
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              required
              value={formData.username}
              onChange={handleInputChange}
              className={`input-subtle w-full px-4 py-3.5 rounded-xl text-base ${
                !usernameValid && formData.username ? 'border-red-500' : 
                usernameAvailable === false ? 'border-red-500' :
                usernameAvailable === true ? 'border-green-500' : ''
              }`}
              placeholder="اسم المستخدم (بالإنجليزية)"
              dir="ltr"
            />
            {renderUsernameStatus()}
            {/* <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium">شروط اسم المستخدم:</p>
              <ul className="text-xs text-blue-600 mt-1 mr-2 space-y-1">
                <li>• 3-30 حرفاً فقط</li>
                <li>• أحرف إنجليزية (a-z, A-Z)</li>
                <li>• أرقام (0-9)</li>
                <li>• شرطة سفلية (_)</li>
                <li>• لا مسافات أو رموز خاصة</li>
              </ul>
            </div> */}
          </div>
        </div>

         <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
           <div>
            <label htmlFor="grade" className="block text-sm font-semibold text-gray-700 mb-2">
              <FaGraduationCap className="ml-1 text-[#320A6B] inline" />
              الصف الدراسي
            </label>
            <select
              id="grade"
              name="grade"
              required
              value={formData.grade}
              onChange={handleInputChange}
              className="input-subtle w-full px-4 py-3.5 rounded-xl text-base custom-select"
            >
              {grades.map((grade, index) => (
                <option key={index} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>

           <div>
            <label htmlFor="school" className="block text-sm font-semibold text-gray-700 mb-2">
              <FaSchool className="ml-1 text-[#B8860B] inline" />
              المدرسة
            </label>
            <input
              type="text"
              id="school"
              name="school"
              required
              value={formData.school}
              onChange={handleInputChange}
              className="input-subtle w-full px-4 py-3.5 rounded-xl text-base"
              placeholder="اسم المدرسة"
            />
          </div>
        </div>

         <div>
          <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
            <FaCity className="ml-1 text-[#B8860B] inline" />
            المدينة
          </label>
          <input
            type="text"
            id="city"
            name="city"
            required
            value={formData.city}
            onChange={handleInputChange}
            className="input-subtle w-full px-4 py-3.5 rounded-xl text-base"
            placeholder="اسم المدينة"
          />
        </div>
      </div>

       <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={nextStep}
          disabled={usernameAvailable === false || (formData.username && usernameAvailable === null) || checkingUsername}
          className="w-full bg-[#320A6B] hover:bg-[#4a1a8c] text-white font-semibold py-3.5 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
        >
          التالي
          <FaArrowLeft className="text-white" />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 flex-1 flex flex-col justify-between">
      <div className="space-y-6">
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
           <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
              <FaLock className="ml-1 text-[#320A6B] inline" />
              كلمة السر
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                required
                minLength="8"
                value={formData.password}
                onChange={handleInputChange}
                className="input-subtle w-full px-4 pl-12 py-3.5 rounded-xl text-base"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">يجب أن تكون 8 أحرف على الأقل</p>
          </div>

           <div>
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
              <FaLock className="ml-1 text-[#320A6B] inline" />
              تأكيد كلمة السر
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                required
                minLength="8"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`input-subtle w-full px-4 pl-12 py-3.5 rounded-xl text-base ${
                  !passwordMatch && formData.confirmPassword ? 'border-red-500' : ''
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {!passwordMatch && formData.confirmPassword && (
              <p className="mt-1 text-xs text-red-500">كلمتا السر غير متطابقتين</p>
            )}
          </div>
        </div>

         <div className="flex items-start">
          <input
            id="terms"
            name="terms"
            type="checkbox"
            required
            checked={formData.terms}
            onChange={handleInputChange}
            className="h-4 w-4 mt-1 text-[#320A6B] border-gray-300 rounded focus:ring-[#320A6B]"
          />
          <label htmlFor="terms" className="mr-2 block text-sm text-gray-700">
            أوافق على
            <a href="#" className="text-[#320A6B] hover:text-[#4a1a8c] font-medium mx-1">الشروط والأحكام</a>
            و
            <a href="#" className="text-[#320A6B] hover:text-[#4a1a8c] font-medium mx-1">سياسة الخصوصية</a>
          </label>
        </div>
      </div>

       <div className="grid grid-cols-2 gap-4 mt-auto pt-6">
        <button
          type="button"
          onClick={prevStep}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
        >
          <FaArrowRight className="text-gray-700" />
          السابق
        </button>
        <button
          type="submit"
          disabled={loading}
          className="bg-[#320A6B] hover:bg-[#4a1a8c] text-white font-semibold py-3.5 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin text-white" />
              جاري الإنشاء...
            </>
          ) : (
            <>
              <FaUserPlus className="text-white" />
              إنشاء الحساب
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid-pattern flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" dir="rtl">
       <div className="absolute top-20 left-10 w-72 h-72 bg-[#320A6B] opacity-5 rounded-full blur-3xl float-animation"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#B8860B] opacity-5 rounded-full blur-3xl float-animation" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#320A6B] opacity-5 rounded-full blur-3xl"></div>

      <div className="relative w-full max-w-2xl">
         <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mt-6">إنشاء حساب جديد</h1>
          <p className="mt-2 text-gray-600">انضم إلى آلاف الطلاب وابدأ رحلتك التعليمية</p>
        </div>

         <div className="flex items-center justify-between mb-8 px-4">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex items-center gap-2 flex-1 justify-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold shadow-lg transition-all duration-300 ${
                  step.completed 
                    ? 'bg-green-500 text-white' 
                    : step.active 
                    ? 'bg-[#320A6B] text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step.completed ? <FaCheck /> : step.icon}
                </div>
                <span className="text-xs font-medium text-gray-700 hidden sm:inline">
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded-full transition-all duration-300 ${
                  step.completed ? 'bg-green-500' : 'bg-gray-200'
                }`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

         <div className="card-subtle rounded-2xl shadow-xl p-8 fixed-form-container">
          <div className="form-content">
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
              {currentStep === 1 ? renderStep1() : renderStep2()}
            </form>
          </div>
        </div>

         <p className="mt-6 text-center text-sm text-gray-600">
          لديك حساب بالفعل؟
          <a href="/login" className="font-semibold text-[#320A6B] hover:text-[#4a1a8c] transition-colors mr-1">
            تسجيل الدخول
          </a>
        </p>
      </div>

       <style jsx>{`
        .grid-pattern {
          background-image:
            linear-gradient(rgba(50, 10, 107, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(50, 10, 107, 0.03) 1px, transparent 1px);
          background-size: 30px 30px;
        }

        .input-subtle {
          background: rgba(255, 255, 255, 0.6);
          border: 1.5px solid rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .input-subtle:focus {
          outline: none;
          border-color: #320A6B;
          background: rgba(255, 255, 255, 0.9);
          box-shadow: 0 0 0 3px rgba(50, 10, 107, 0.1);
        }

        .card-subtle {
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.08);
          backdrop-filter: blur(20px);
          min-height: 500px;
          display: flex;
          flex-direction: column;
        }

        .fixed-form-container {
          min-height: 500px;
          display: flex;
          flex-direction: column;
        }
        
        .form-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .custom-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: left 0.75rem center;
          background-size: 1.25rem;
          padding-left: 2.5rem;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .float-animation {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>

      <style jsx global>{`
        body {
          font-family: 'Cairo', sans-serif;
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;